import { INLINE_LINK } from '@/components/bedrock';
import { useT } from '@/core/I18nProvider';
import { LegalPage } from '@/features/legal/LegalPage';
import {
  anthropicCardBodyKey,
  openaiCardBodyKey,
} from '@/features/trust/content/provider-contracts';
import { useLocalizedSeoMeta } from '@/lib/seo/use-localized-seo-meta';

import { AiComplianceFooterSections } from './AiComplianceFooterSections';

import type { ReactElement } from 'react';

/**
 * Trust Center — AI compliance page (`/trust/ai`). Phase 6 Pass 3
 * iter 248.
 *
 * Per `pass-3-trust-center.md` § Pages → `/trust/ai`: "AI
 * disclosure; model cards; system card; eval methodology; bias
 * evaluation." Content authority: `/product/trust/compliance.md`
 * § EU AI Act + BL-014 disclosure copy commitments.
 *
 * **Marketing voice (Marketer hat, locked)**: concrete legal
 * citations (Article 50 / Article 53 / Article 5 / Article 9 /
 * Recital 89 / fines €35M / 7% / 2 Aug 2026 enforcement) + named
 * model providers (Anthropic / OpenAI) + in-region retrieval on
 * Amazon Bedrock (Titan embeddings + Bedrock rerank, eu-central-1) + the
 * AI disclosure the widget ACTUALLY shows ("This is a conversation
 * with an AI assistant." — the product-experience spec copy, quoted
 * per-locale from the widget catalogs) + ZDR contract status per
 * provider.
 *
 * **Architect hat (locked)**: the page is the public projection
 * of the AI-disclosure copy locked for BL-014. The English version
 * is the legally authoritative one (see `<LegalTranslationNotice />`).
 * When the disclosure copy changes, this page changes in the same PR.
 *
 * **Designer hat (locked)**: 11-section layout — Classification /
 * Transparency obligations / Penalties / Model cards / System card
 * / Evaluation methodology / Bias evaluation / AUP / Counsel
 * review / Related / Contact.
 *
 * **Three-layer contract preserved**: pure presentational. No
 * data fetch.
 */
export function AiCompliance(): ReactElement {
  const { t } = useT();
  useLocalizedSeoMeta({
    title: t('ai.seo.title'),
    description: t('ai.seo.description'),
    path: '/trust/ai',
  });
  return (
    <LegalPage
      breadcrumb={t('ai.backToTrust')}
      title={t('ai.title')}
      intro={t('ai.intro')}
      lastUpdatedSlug="ai-compliance"
    >
      <section aria-labelledby="ai-classification" className="mb-10">
        <h2
          id="ai-classification"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('ai.classification.heading')}
        </h2>
        <p className="mb-3">{t('ai.classification.p1')}</p>
        <p>{t('ai.classification.p2')}</p>
      </section>

      <section aria-labelledby="ai-article-50" className="mb-10">
        <h2
          id="ai-article-50"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('ai.article50.heading')}
        </h2>
        <p className="mb-3">{t('ai.article50.intro')}</p>
        <ol className="list-decimal space-y-2 ps-6">
          <li>
            <strong>{t('ai.article50.item1.lead')}</strong> {t('ai.article50.item1.body')}
          </li>
          <li>
            <strong>{t('ai.article50.item2.lead')}</strong> {t('ai.article50.item2.body')}
          </li>
          <li>
            <strong>{t('ai.article50.item3.lead')}</strong> {t('ai.article50.item3.body')}
          </li>
          <li>
            <strong>{t('ai.article50.item4.lead')}</strong> {t('ai.article50.item4.body')}
          </li>
        </ol>
      </section>

      <section aria-labelledby="ai-provenance" className="mb-10">
        <h2
          id="ai-provenance"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('ai.provenance.heading')}
        </h2>
        <p className="mb-3">{t('ai.provenance.p1')}</p>
        <p>
          {t('ai.provenance.p2')}{' '}
          <a className={INLINE_LINK} href="/.well-known/syncanix-content-provenance">
            {t('ai.provenance.keysLink')}
          </a>{' '}
          ·{' '}
          <a className={INLINE_LINK} href="/trust/ai-content-provenance-spec.md">
            {t('ai.provenance.specLink')}
          </a>
        </p>
      </section>

      <section aria-labelledby="ai-deployer" className="mb-10">
        <h2
          id="ai-deployer"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('ai.deployer.heading')}
        </h2>
        <p className="mb-4">{t('ai.deployer.intro')}</p>
        <h3 className="mb-2 mt-7 text-[1.08rem] font-semibold tracking-[-0.024em] text-[color:var(--bd-ink)]">
          {t('ai.deployer.syncanix.heading')}
        </h3>
        <ul className="mb-5 list-disc space-y-1 ps-6" data-deployer="syncanix">
          <li>{t('ai.deployer.syncanix.disclosure')}</li>
          <li>{t('ai.deployer.syncanix.locales')}</li>
          <li>{t('ai.deployer.syncanix.techfile')}</li>
          <li>{t('ai.deployer.syncanix.marking')}</li>
        </ul>
        <h3 className="mb-2 mt-7 text-[1.08rem] font-semibold tracking-[-0.024em] text-[color:var(--bd-ink)]">
          {t('ai.deployer.yours.heading')}
        </h3>
        <ul className="list-disc space-y-1 ps-6" data-deployer="yours">
          <li>{t('ai.deployer.yours.customUi')}</li>
          <li>{t('ai.deployer.yours.handoff')}</li>
          <li>{t('ai.deployer.yours.downstream')}</li>
          <li>{t('ai.deployer.yours.sector')}</li>
          <li>{t('ai.deployer.yours.privacy')}</li>
        </ul>
        <p className="mt-4 text-sm text-[color:var(--bd-muted)]">{t('ai.deployer.caveat')}</p>
      </section>

      <section aria-labelledby="ai-penalties" className="mb-10">
        <h2
          id="ai-penalties"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('ai.penalties.heading')}
        </h2>
        <ul className="list-disc space-y-1 ps-6">
          <li>{t('ai.penalties.item1')}</li>
          <li>{t('ai.penalties.item2')}</li>
          <li>{t('ai.penalties.item3')}</li>
        </ul>
      </section>

      <section aria-labelledby="ai-model-cards" className="mb-10">
        <h2
          id="ai-model-cards"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('ai.modelCards.heading')}
        </h2>
        <p className="mb-3">{t('ai.modelCards.intro')}</p>
        <ul className="list-disc space-y-2 ps-6">
          <li>
            <strong>{t('ai.modelCards.item1.lead')}</strong> {t(anthropicCardBodyKey())}
          </li>
          <li>
            <strong>{t('ai.modelCards.item2.lead')}</strong> {t(openaiCardBodyKey())}
          </li>
          <li>
            <strong>{t('ai.modelCards.item3.lead')}</strong> {t('ai.modelCards.item3.body')}
          </li>
          <li>
            <strong>{t('ai.modelCards.item4.lead')}</strong> {t('ai.modelCards.item4.body')}
          </li>
        </ul>
        <p className="mt-3">{t('ai.modelCards.byok')}</p>
      </section>

      <section aria-labelledby="ai-system-card" className="mb-10">
        <h2
          id="ai-system-card"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('ai.systemCard.heading')}
        </h2>
        <p className="mb-3">{t('ai.systemCard.p1')}</p>
        <p>{t('ai.systemCard.p2')}</p>
      </section>

      <section aria-labelledby="ai-eval" className="mb-10">
        <h2
          id="ai-eval"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('ai.eval.heading')}
        </h2>
        <p className="mb-3">{t('ai.eval.intro')}</p>
        <ul className="list-disc space-y-1 ps-6">
          <li>
            <strong>{t('ai.eval.item1.lead')}</strong> {t('ai.eval.item1.body')}
          </li>
          <li>
            <strong>{t('ai.eval.item2.lead')}</strong> {t('ai.eval.item2.body')}
          </li>
          <li>
            <strong>{t('ai.eval.item3.lead')}</strong> {t('ai.eval.item3.body')}
          </li>
          <li>
            <strong>{t('ai.eval.item4.lead')}</strong> {t('ai.eval.item4.body')}
          </li>
        </ul>
      </section>

      <section aria-labelledby="ai-bias" className="mb-10">
        <h2
          id="ai-bias"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('ai.bias.heading')}
        </h2>
        <p>{t('ai.bias.body')}</p>
      </section>

      <section aria-labelledby="ai-aup" className="mb-10">
        <h2
          id="ai-aup"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('ai.aup.heading')}
        </h2>
        <p className="mb-3">{t('ai.aup.intro')}</p>
        <ul className="list-disc space-y-1 ps-6">
          <li>{t('ai.aup.item1')}</li>
          <li>{t('ai.aup.item2')}</li>
          <li>{t('ai.aup.item3')}</li>
          <li>{t('ai.aup.item4')}</li>
          <li>{t('ai.aup.item5')}</li>
          <li>{t('ai.aup.item6')}</li>
          <li>{t('ai.aup.item7')}</li>
          <li>{t('ai.aup.item8')}</li>
          <li>{t('ai.aup.item9')}</li>
        </ul>
        <p className="mt-3">{t('ai.aup.outro')}</p>
      </section>

      <section aria-labelledby="ai-counsel" className="mb-10">
        <h2
          id="ai-counsel"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('ai.counsel.heading')}
        </h2>
        <p>
          {t('ai.counsel.body1')} <code>docs/legal/counsel-sign-off.md</code>{' '}
          {t('ai.counsel.body2')}
        </p>
      </section>

      <AiComplianceFooterSections />
    </LegalPage>
  );
}
